import { InspectionFile } from '@detail/components/InspectionDetail/types'
import {
  HydratedInspectionDetailResponseBody,
  ImageInferenceAttribute,
  ListInspectionEventsResponseBody,
  ListAssetInferenceChangelogsRequestQuery,
  ListInferenceChangelogsResponseBody,
  PatchInferenceResultRequestBody,
  PatchInferenceResultResponseBody,
  RetrieveInferenceJSONResponseBody,
  TrackingEvent,
} from 'bff'
import { createStore } from 'zustand/vanilla'
import { devtools } from 'zustand/middleware'
import {
  ListInspectionChangelogsRequestQuery,
  ListInspectionChangelogsResponseBody,
} from 'bff/contracts/inference-changelog.contract'
import { InspectionDetailDamagesResponseBody } from 'bff/contracts/inspection.contract'
import { isCompleteStage } from '@detail/utils/isCompleteStage'

export interface InferenceAttributeValueProps {
  asset: Pick<InspectionFile, 'inferences' | 'edges' | 'validations'> & {
    instance: {
      _id: string
    }
    spec: {
      identifier: string
    }
  }
  inference: ImageInferenceAttribute
}

export interface InspectionDetailComponents {
  InferenceAttributeValue?: React.FC<InferenceAttributeValueProps>
  ExtraAssetMetadata?: React.FC<{
    asset: InspectionFile
  }>
}

export interface InspectionDetailRPC {
  recalculateInspectionResult: (input: {
    params: {
      inspectionId: string
    }
  }) => Promise<void>
  listInspectionChangelogs: (input: {
    params: {
      inspectionId: string
    }
    query: ListInspectionChangelogsRequestQuery
  }) => Promise<ListInspectionChangelogsResponseBody>
  listInferenceChangelogs: (input: {
    params: {
      inspectionId: string
    }
    query: ListAssetInferenceChangelogsRequestQuery
  }) => Promise<ListInferenceChangelogsResponseBody>
  retrieveInspectionDamages: (input: {
    params: {
      inspectionId: string
    }
  }) => Promise<InspectionDetailDamagesResponseBody>
  retrieveInferenceResult: (input: {
    params: {
      inferenceResultId: string
    }
  }) => Promise<RetrieveInferenceJSONResponseBody>
  patchInferenceResult: (input: {
    body: PatchInferenceResultRequestBody
    params: {
      inferenceResultId: string
    }
  }) => Promise<PatchInferenceResultResponseBody>
}

export type InspectionDetailContext = 'pdf' | 'web'

type DashboardInspectionTrackingEvent =
  ListInspectionEventsResponseBody['events'][number]

type InspectionDetailTrackingEventMetadata =
  DashboardInspectionTrackingEvent['metadata'] &
    Partial<TrackingEvent['metadata']>

export type InspectionDetailTrackingEvent = Pick<
  DashboardInspectionTrackingEvent,
  'action' | 'scope' | 'createdAt'
> &
  Partial<
    Pick<
      DashboardInspectionTrackingEvent,
      '_id' | 'clientIP' | 'endpoint' | 'latitude' | 'longitude' | 'useragent'
    >
  > &
  Partial<
    Pick<
      TrackingEvent,
      'id' | 'cookies' | 'updatedAt' | 'clientIP' | 'endpoint' | 'useragent'
    >
  > & {
    metadata: InspectionDetailTrackingEventMetadata
  }

export interface InspectionDetailState
  extends HydratedInspectionDetailResponseBody {
  platform: 'office' | 'dashboard'
  tracking: InspectionDetailTrackingEvent[]
  stickyNavbarWidth?: number
  context: InspectionDetailContext
  inspectionFiles: InspectionFile[]
  focusedFileId?: string | null
  authUser: {
    email: string
  }
}

export type InspectionDetailActions = {
  updateStickyNavbarWidth: (width?: number) => void
  updateFileSources: (
    fileId: string,
    sources: Partial<Pick<InspectionFile, 'src' | 'previewURL'>>,
  ) => void
  setFocusedFileId: (fileId?: string | null) => void
  updateCompleteSectionFieldValues: (
    assets: Array<{
      identifier: string
      value: unknown
    }>,
  ) => void
  updateInferenceValue: (input: {
    value: string
    inferenceResultValue?: any
    modelBindingGroupVersionTransformationId: string
  }) => void
}

export type InspectionDetailStore = InspectionDetailState &
  InspectionDetailActions

export const defaultInitState: Partial<InspectionDetailState> = {
  inspectionFiles: [],
  tracking: [],
}

const patchInputFieldValue = ({
  field,
  assets,
}: {
  field: InspectionDetailState['sections'][number]['fields'][number]
  assets: Array<{
    identifier: string
    value: unknown
  }>
}) => {
  if (!field || typeof field !== 'object') return field

  if (!('identifier' in field)) return field

  const targetIdentifier = field.identifier

  const matchedAsset = assets.find(
    (asset) => asset.identifier === targetIdentifier,
  )

  if (!matchedAsset) return field

  return {
    ...field,
    value: matchedAsset.value,
  }
}

export const createInspectionDetailStore = (
  //@ts-ignore
  initState: InspectionDetailState = defaultInitState,
) => {
  return createStore<InspectionDetailStore>()(
    devtools((set) => ({
      ...initState,
      updateStickyNavbarWidth: (width) =>
        set((state) => ({ stickyNavbarWidth: width })),
      setFocusedFileId: (fileId) => set((state) => ({ focusedFileId: fileId })),
      updateInferenceValue: (input) => {
        set((state) => {
          return {
            ...state,
            inspectionFiles: state.inspectionFiles.map((inspectionFile) => {
              return {
                ...inspectionFile,
                inferences: inspectionFile.inferences.map((inference) => {
                  if (
                    inference.modelBindingGroupVersionTransformationId ===
                    input.modelBindingGroupVersionTransformationId
                  )
                    return {
                      ...inference,
                      value: input.value,
                      inferenceResultValue:
                        input.inferenceResultValue ||
                        inference.inferenceResultValue,
                      latestChangelog: {
                        ...(inference.latestChangelog || {}),
                        patch: {
                          ...(inference.latestChangelog || {}).patch,
                          value: input.value,
                        },
                      },
                    }

                  return inference
                }),
              }
            }),
          }
        })
      },
      updateFileSources: (fileId, sources) => {
        set((state) => {
          return {
            ...state,
            inspectionFiles: state.inspectionFiles.map((inspectionFile) => {
              if (inspectionFile.identifier === fileId)
                return {
                  ...inspectionFile,
                  ...sources,
                }

              return inspectionFile
            }),
          }
        })
      },
      updateCompleteSectionFieldValues: (assets) => {
        set((state) => {
          return {
            ...state,
            sections: (state.sections || []).map((section) => {
              if (!isCompleteStage(section.stage)) return section

              return {
                ...section,
                fields: section.fields.map((field) =>
                  patchInputFieldValue({
                    field,
                    assets,
                  }),
                ),
              }
            }),
            template: {
              ...state.template,
              inputs: state.template.inputs.map((input) => {
                const matchedAsset = assets.find(
                  (asset) => asset.identifier === input.identifier,
                )

                if (!matchedAsset) return input

                return {
                  ...input,
                  value: matchedAsset.value,
                }
              }),
            },
          }
        })
      },
    })),
  )
}
